import { Hash, LucideIcon, MessageCircle, Send, Webhook } from 'lucide-react';

import type { ArdiChannel, ChannelKind } from '../../../common/hooks/use-ardi-api.hook';

/** Neutral glyphs for the four ways to reach Ardi — no brand colours on chrome. */
export const CHANNEL_ICONS: Record<ChannelKind, LucideIcon> = {
  whatsapp: MessageCircle,
  telegram: Send,
  slack: Hash,
  webhook: Webhook,
};

export const CHANNEL_KINDS: ChannelKind[] = ['whatsapp', 'telegram', 'slack', 'webhook'];

export const channelKindKey = (kind: ChannelKind) => `ARDI.CHANNELS.KIND.${kind.toUpperCase()}`;

/**
 * One word for where a channel stands:
 * - `needs_pairing`: credentials work but no chat is linked yet
 * - `live`: at least one chat (or a webhook with its token) can reach Ardi
 * - `error`: the provider refused us — reconnect
 * - `setup`: created, credentials not given yet
 */
export type ChannelPhase = 'setup' | 'needs_pairing' | 'live' | 'error';

export function channelPhase(channel: Pick<ArdiChannel, 'status' | 'kind' | 'links' | 'needsReconnect'>): ChannelPhase {
  if (channel.status === 'error' || channel.needsReconnect) return 'error';
  if (channel.status === 'pending') return 'setup';
  if (channel.kind === 'webhook') return 'live';
  return channel.links.length ? 'live' : 'needs_pairing';
}

/** Can this channel receive notifications at all? */
export const deliverable = (channel: ArdiChannel) =>
  channel.status === 'connected' && (channel.kind === 'webhook' ? !!channel.info?.outboundUrl : channel.links.length > 0);
